import gettext from '@sdk/lang/gettext';
import { FormError } from '@shopify/react-form';
import { useSession } from '@sdk/session/SessionContext';
import isOrgFeatureEnabled from '@sdk/authentication/helpers/isOrgFeatureEnabled';
import { Feature } from '@sdk/common/features';
import { RestrictionType } from '@sdk/messages/types/RestrictionType';
import { RESTRICTION_OPTIONS } from '@sdk/messages/constants';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Picker from '@sdk/components/forms/Picker';
import FieldLabel from '@sdk/components/forms/components/FieldLabel';

import { PremiumModalNames } from '@web/premiumGuard/constants/modalNames';
import PremiumGuard from '@web/premiumGuard/PremiumGuard';
import { FieldWrapper } from '@web/conversations/ComposeConversation/composeConversation.styles';
import { RestrictedToUsersField } from '@web/conversations/ComposeConversation/RestrictedToUsersField/RestrictedToUsersField';
import usePremiumGuard from '@web/premiumGuard/usePremiumGuard';
import getPremiumTooltipId from '@web/premiumGuard/getPremiumTooltipId';
import PremiumGuardLabel from '@web/premiumGuard/PremiumGuardLabel';
import {
  MembersField,
  RestrictedField,
  RestrictedToUsersField as RestrictedToUsersFieldType,
} from '@web/conversations/types/ConversationFields';
import { PremiumGuardInlineContainer } from '@web/premiumGuard/premiumGuardLabel.styles';

type Props = {
  members: MembersField;
  restricted: RestrictedField;
  restrictedToUsers: RestrictedToUsersFieldType;
  submitErrors: FormError[];
};

export const ConversationRestrictionsField = ({
  members,
  restricted,
  restrictedToUsers,
  submitErrors,
}: Props) => {
  const { currentUser } = useSession();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const premiumGuardConfig = getPremiumGuardConfig(Feature.conversationRestrictions);

  return (
    <PremiumGuard
      hasAccess={isOrgFeatureEnabled(Feature.conversationRestrictions, currentUser)}
      premiumGuardModalName={PremiumModalNames.conversationRestrictions}
      premiumFeature={Feature.conversationRestrictions}
      tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.conversationRestrictions)]}>
      <>
        <FieldWrapper>
          <FieldLabel label={gettext('restrictions')} htmlFor="conversation-restrictions">
            {!premiumGuardConfig.hasAccess && (
              <PremiumGuardInlineContainer>
                <PremiumGuardLabel
                  useLabelForSelectBox
                  pLeft={5}
                  pBottom={3}
                  label={premiumGuardConfig.requiredPlan}
                  tooltipId={premiumGuardConfig.tooltipId}
                  tooltipText={premiumGuardConfig.tooltipContent}
                />
              </PremiumGuardInlineContainer>
            )}

            <Picker
              options={RESTRICTION_OPTIONS}
              value={restricted.value}
              onChange={(value: RestrictionType): void => restricted.onChange(value)}
            />
          </FieldLabel>
        </FieldWrapper>

        <CollapsibleSection
          headerItem={null}
          isActive={restricted.value === RestrictionType.SELECTED_ONLY}
          contentItem={
            <RestrictedToUsersField
              members={members}
              restrictedToUsers={restrictedToUsers}
              hasError={Boolean(restrictedToUsers.error && submitErrors.length)}
            />
          }
        />
      </>
    </PremiumGuard>
  );
};
